import { apiClient } from "../config/axiosConfig";
import {
  ENCODER_BASE_ENDPOINT,
  GET_ENCODER_CATALOG_ENDPOINT,
  GET_ENCODER_CLINICAL_CRITERIA_ENDPOINT,
  GET_ENCODER_CM_EXPLORER_ENDPOINT,
  GET_ENCODER_CODES_SEARCH_ENDPOINT,
  GET_ENCODER_COVERAGE_ENDPOINT,
  GET_ENCODER_GUIDELINES_ENDPOINT,
  GET_ENCODER_MSDRG_ENDPOINT,
  GET_ENCODER_PCS_EXPLORER_ENDPOINT,
  POST_ENCODER_CODES_CPT_EDITS_ENDPOINT,
  POST_ENCODER_CODES_INSTRUCTIONS_ENDPOINT,
  POST_ENCODER_CODES_VALIDATE_ENDPOINT,
} from "../constants/arcplus-endpoint-constants";
import type {
  CmSearchSource,
  CmTabularSearchMode,
  EncoderGuidelineType,
  MedicareCodeEditorRequest,
  MsDrgGroupRequest,
} from "../types/encoder";
import { encoderQueryParams } from "../utils/encoder";

const ENCODER_TIMEOUT_MS = 60_000;

const encoderGet = async (
  url: string,
  params?: Record<string, unknown>
) => {
  const response = await apiClient.get(url, {
    params: params ? encoderQueryParams(params) : undefined,
    timeout: ENCODER_TIMEOUT_MS,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};

const encoderPost = async (url: string, body: unknown) => {
  const response = await apiClient.post(url, body, {
    timeout: ENCODER_TIMEOUT_MS,
  });
  return response.data;
};

export const getEncoderCatalog = () => encoderGet(GET_ENCODER_CATALOG_ENDPOINT);

export const searchEncoderCodes = (params: {
  q: string;
  type?: string;
  year?: number;
  limit?: number;
}) => encoderGet(GET_ENCODER_CODES_SEARCH_ENDPOINT, params);

export const postEncoderCodeInstructions = (codes: string[]) =>
  encoderPost(POST_ENCODER_CODES_INSTRUCTIONS_ENDPOINT, { codes });

export const postEncoderCodeValidate = (codes: string[]) =>
  encoderPost(POST_ENCODER_CODES_VALIDATE_ENDPOINT, { codes });

export const postEncoderCptEdits = (codes: string[], dateofService: string) =>
  encoderPost(POST_ENCODER_CODES_CPT_EDITS_ENDPOINT, { codes, dateofService });

export const getEncoderCmYears = () =>
  encoderGet(`${GET_ENCODER_CM_EXPLORER_ENDPOINT}/years`);

export const getEncoderCmIndexSearch = (params: {
  year: number;
  source: CmSearchSource;
  q: string;
}) =>
  encoderGet(`${GET_ENCODER_CM_EXPLORER_ENDPOINT}/index/search`, {
    ...params,
    year: String(params.year),
  });

export const getEncoderCmIndexTree = (params: {
  year: number;
  source: string;
  termId: string;
}) =>
  encoderGet(`${GET_ENCODER_CM_EXPLORER_ENDPOINT}/index/tree`, {
    ...params,
    year: String(params.year),
  });

export const getEncoderCmTable = (params: {
  year: number;
  source: string;
  q?: string;
}) =>
  encoderGet(`${GET_ENCODER_CM_EXPLORER_ENDPOINT}/table`, {
    ...params,
    year: String(params.year),
  });

export const getEncoderCmTabular = (params: { year: number; code: string }) =>
  encoderGet(`${GET_ENCODER_CM_EXPLORER_ENDPOINT}/tabular`, {
    ...params,
    year: String(params.year),
  });

export const getEncoderCmTabularSearch = (params: {
  year: number;
  mode: CmTabularSearchMode;
  q: string;
}) =>
  encoderGet(`${GET_ENCODER_CM_EXPLORER_ENDPOINT}/tabular-search`, {
    ...params,
    year: String(params.year),
  });

export const getEncoderPcsYears = () =>
  encoderGet(`${GET_ENCODER_PCS_EXPLORER_ENDPOINT}/years`);

export const getEncoderPcsTables = (fiscalYear: number) =>
  encoderGet(`${GET_ENCODER_PCS_EXPLORER_ENDPOINT}/tables`, { fiscalYear });

export const getEncoderPcsIndexSearch = (params: {
  fiscalYear: number;
  q: string;
  limit?: number;
}) => encoderGet(`${GET_ENCODER_PCS_EXPLORER_ENDPOINT}/index/search`, params);

export const getEncoderPcsDefinitionsSearch = (params: {
  fiscalYear: number;
  q: string;
  limit?: number;
}) =>
  encoderGet(`${GET_ENCODER_PCS_EXPLORER_ENDPOINT}/definitions/search`, params);

export const getEncoderPcsDefinitionLookup = (params: {
  fiscalYear: number;
  section: string;
  pos: number;
  label: string;
}) =>
  encoderGet(`${GET_ENCODER_PCS_EXPLORER_ENDPOINT}/definitions/lookup`, params);

export const getEncoderPcsCodeDescription = (params: {
  fiscalYear: number;
  code: string;
}) =>
  encoderGet(`${GET_ENCODER_PCS_EXPLORER_ENDPOINT}/code-description`, params);

export const getEncoderIcdGuidelineYears = (guidelineType?: EncoderGuidelineType) =>
  encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/icd/years`, { guidelineType });

export const getEncoderIcdChapters = (guidelineType: EncoderGuidelineType) =>
  encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/icd/chapters`, {
    guidelineType,
  });

export const getEncoderIcdSubChapters = (params: {
  guidelineType: EncoderGuidelineType;
  chapter: string;
}) => encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/icd/sub-chapters`, params);

export const getEncoderIcdTopics = (params: {
  guidelineType: EncoderGuidelineType;
  subChapter: string;
}) => encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/icd/topics`, params);

export const getEncoderIcdSubTopics = (params: {
  guidelineType: EncoderGuidelineType;
  topic: string;
}) => encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/icd/sub-topics`, params);

export const searchEncoderIcdGuidelines = (params: {
  guidelineType?: EncoderGuidelineType;
  keyword?: string;
  year?: string;
  chapter?: string;
  subChapter?: string;
  topic?: string;
  subTopic?: string;
  includeBody?: boolean;
}) => encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/icd/search`, params);

export const getEncoderIcdGuidelinesByIds = (ids: Array<number | string>) =>
  encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/icd/by-ids`, {
    ids: ids.join(","),
  });

export const getEncoderAhaYears = () =>
  encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/aha/years`);

export const getEncoderAhaQuarters = (year: string) =>
  encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/aha/quarters`, { year });

export const searchEncoderAhaGuidelines = (params: {
  keyword?: string;
  year?: string;
  quarter?: string;
  quarters?: string[];
  includeBody?: boolean;
  cmContext?: boolean;
  referenceCode?: string;
  referenceCategory?: string;
}) => encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/aha/search`, params);

export const getEncoderAhaGuidelinesByIds = (ids: Array<number | string>) =>
  encoderGet(`${GET_ENCODER_GUIDELINES_ENDPOINT}/aha/by-ids`, {
    ids: ids.join(","),
  });

export const getEncoderClinicalCriteriaFiles = () =>
  encoderGet(`${GET_ENCODER_CLINICAL_CRITERIA_ENDPOINT}/files`);

export const getEncoderClinicalCriteriaFile = (params: {
  id?: string;
  name?: string;
}) => encoderGet(`${GET_ENCODER_CLINICAL_CRITERIA_ENDPOINT}/file`, params);

export const getEncoderMsdrgOptions = () =>
  encoderGet(`${GET_ENCODER_MSDRG_ENDPOINT}/options`);

export const postEncoderMsdrgGroup = (body: MsDrgGroupRequest) =>
  encoderPost(`${GET_ENCODER_MSDRG_ENDPOINT}/group`, body);

export const postEncoderMsdrgSexRestrictions = (body: {
  diagnosisCodes: string[];
  procedureCodes: string[];
}) => encoderPost(`${GET_ENCODER_MSDRG_ENDPOINT}/sex-restrictions`, body);

export const postEncoderMsdrgElixhauser = (body: Record<string, unknown>) =>
  encoderPost(`${GET_ENCODER_MSDRG_ENDPOINT}/elixhauser`, body);

export const postEncoderMsdrgMceEdit = (body: MedicareCodeEditorRequest) =>
  encoderPost(`${GET_ENCODER_MSDRG_ENDPOINT}/mce/edit`, body);

export const getEncoderCoverageStates = () =>
  encoderGet(`${GET_ENCODER_COVERAGE_ENDPOINT}/states`);

export const getEncoderCoverage = (params: { code: string; state?: string }) =>
  encoderGet(GET_ENCODER_COVERAGE_ENDPOINT, params);

export const postEncoderCoverageValidate = (body: {
  procedures: string[];
  diagnoses: string[];
  state?: string;
}) => encoderPost(`${GET_ENCODER_COVERAGE_ENDPOINT}/validate`, body);

export const getEncoderCoverageLcdDocument = (lcdId: string) =>
  encoderGet(`${GET_ENCODER_COVERAGE_ENDPOINT}/lcd-document`, { lcdId });

export { ENCODER_BASE_ENDPOINT };
